import axios from 'axios'
import { i18n } from '../../plugins/i18n'
import { castAttributesForCamelCase, castAttributesForSnakeCase } from '../../modules/string_converter'

const initialState = () => {
  return {
    data: {
      basic: {
        templateName: '',
        showCoverForm: false,
        mediaImage: null,
        coverTitle: i18n.t('components.CompaniesSurveyTemplateForm.basic.default.coverTitle'),
        coverText: i18n.t('components.CompaniesSurveyTemplateForm.basic.default.coverText'),
        showEmailForm: false,
        supportEmail: '',
        requiredEmail: false,
        privacyPolicyBody: '',
        showPrivacyPolicyForm: false,
        changePublishState: false, // 公開状態を変更するかどうか
      },
      question: {
        questions: [{
          name: '',
          description: '',
          required: false,
          questionType: 'one_line_text',
          options: [{ name: '' }]
        }],
        nps: {
          name: i18n.t('components.CompaniesSurveyTemplateForm.question.default.npsTitle'),
          description: '',
          required: false,
          questionType: 'nps'
        },
        showNps: false,
      },
      complete: {
        mediaImage: null,
        title: i18n.t('components.CompaniesSurveyTemplateForm.complete.default.title'),
        text: i18n.t('components.CompaniesSurveyTemplateForm.complete.default.text'),
        showBtnForm: false,
        btnLabel: '',
        btnLink: ''
      },
      guideToReviewSite: {
        delete: true,
        title: i18n.t('components.CompaniesSurveyTemplateForm.complete.default.guideToReviewSiteTitle'),
        displayLocation: 'survey_complete',
      },
      brand: {
        brands: [],
        brandIds: [],
      },
    },
    meta: {
      status: 'unpublished',
      questionEditable: true,
      basicEditable: true,
      completeEditable: true,
    },
    loading: false,
  }
}

export const companiesCustomSurvey = {
  namespaced: true,
  state: initialState(),
  mutations: {
    apiConnect(state) {
      state.loading = true
      state.loadError = false
    },
    readCustomSurveySuccess(state, payload) {
      const data = payload.data
      const questions = data.question.items.filter(v => v.questionType !== 'nps').map(v => {
        if (!v.options) v.options = []
        return v
      })
      const nps = data.question.items.find(v => v.questionType === 'nps')

      state.meta = {
        status: payload.meta.status,
        basicEditable: payload.meta.basicInfoEditable,
        questionEditable: payload.meta.questionEditable,
        completeEditable: payload.meta.completeEditable,
      }
      state.data = {
        basic: {
          ...state.data.basic,
          templateName: data.basicInfo.name,
          changePublishState: false,
        },
        question: {
          ...state.data.question,
          questions: questions,
        },
        complete: {
          ...state.data.complete,
          mediaImage: data.complete.mediaImage,
          title: data.complete.title,
          text: data.complete.description,
        },
        guideToReviewSite: {
          ...state.data.guideToReviewSite,
        },
        brand: {
          ...state.data.brand,
        },
      }
      if (data.basicInfo.cover) {
        state.data.basic.coverTitle = data.basicInfo.cover.title
        state.data.basic.coverText = data.basicInfo.cover.description
        state.data.basic.mediaImage = data.basicInfo.cover.mediaImage
        state.data.basic.showCoverForm = true
      }
      if (data.basicInfo.answererEmailSetting) {
        state.data.basic.showEmailForm = true
        state.data.basic.supportEmail = data.basicInfo.answererEmailSetting.supportEmail
        state.data.basic.requiredEmail = data.basicInfo.answererEmailSetting.required
        state.data.basic.privacyPolicyBody = data.basicInfo.answererEmailSetting.privacyPolicyBody
      }
      if (nps) {
        state.data.question.nps = nps
        state.data.question.showNps = true
      }
      if (data.complete.button) {
        state.data.complete.showBtnForm = true
        state.data.complete.btnLabel = data.complete.button.label
        state.data.complete.btnLink = data.complete.button.link
      }
      if (data.guideToReviewSite) {
        state.data.guideToReviewSite.delete = false
        state.data.guideToReviewSite.title = data.guideToReviewSite.title
        state.data.guideToReviewSite.displayLocation = data.guideToReviewSite.displayLocation
      }
      if (data.brands) {
        state.data.brand.brands = data.brands
      }
      state.loading = false
    },
    createCustomSurveySuccess(state) {
      state.loading = false
    },
    updateCustomSurveySuccess(state) {
      state.loading = false
    },
    apiFailure(state) {
      state.loading = false
    },
    setAttribute(state, { tab, key, value }) {
      state.data = {
        ...state.data,
        [tab]: {
          ...state.data[tab],
          [key]: value
        }
      }
    },
    resetCustomSurvey(state) {
      Object.assign(state, initialState())
    },
    changeCustomSurveyForCopy(state) {
      state.data.brand = initialState().data.brand
      state.data.basic.templateName = i18n.t('labels.copyOf', { name: state.data.basic.templateName })
      state.meta = initialState().meta
    },
  },
  actions: {
    changeCustomSurveyForCopy({ commit }) {
      commit('changeCustomSurveyForCopy')
    },
    resetCustomSurvey({ commit }) {
      commit('resetCustomSurvey')
    },
    changeAttribute({ commit }, { tab, key, value }) {
      commit('setAttribute', { tab, key, value })
    },
    readCustomSurvey({ commit, rootState }, id) {
      commit('resetCustomSurvey')
      commit('apiConnect')
      return axios
        .get('/api/companies/' + rootState.route.params.company_uuid + '/custom_surveys/' + id +'.json')
        .then(res => {
          commit('readCustomSurveySuccess', castAttributesForCamelCase(res.data))
        })
        .catch(err => {
          commit('apiFailure')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          console.error(err)
        })
    },
    createCustomSurvey({ commit, rootState }, payload) {
      commit('apiConnect')
      return axios
        .post('/api/companies/' + rootState.route.params.company_uuid + '/custom_surveys.json', castAttributesForSnakeCase(payload))
        .then(res => {
          commit('createCustomSurveySuccess')
          commit('readCustomSurveySuccess', castAttributesForCamelCase(res.data))
          commit('flash/updateFlash', { type: 'notice', message: i18n.t('messages.created', { value: i18n.t('models.survey.modelName') }) }, { root: true })
          return res.data.data.id
        })
        .catch(err => {
          commit('apiFailure')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          console.error(err)
        })
    },
    updateCustomSurvey({ commit, rootState }, payload) {
      commit('apiConnect')
      return axios
        .put('/api/companies/' + rootState.route.params.company_uuid + '/custom_surveys/' + rootState.route.params.survey_id +'.json', castAttributesForSnakeCase(payload))
        .then(res => {
          commit('updateCustomSurveySuccess')
          commit('readCustomSurveySuccess', castAttributesForCamelCase(res.data))
          commit('flash/updateFlash', { type: 'notice', message: i18n.t('messages.updatedInfo', { value: i18n.t('models.survey.modelName') }) }, { root: true })
        })
        .catch(err => {
          commit('apiFailure')
          commit('flash/updateFlash', { type: 'alert', message: err.response.data.errors[0].detail }, { root: true })
          console.error(err)
        })
    },
  }
}
